import React, { useState, useEffect, useContext, useCallback } from "react";
import ChangePasswordModal from "./ChangePassword";
import EditUserDataModal from "./EditUserDataModal";
import TripConfirmation from "./TripConfirmation";
import ShareGroupPreferences from "./ShareGroupPreferences";
import { useNavigate } from "react-router-dom";
import { editUser, getUserTrips } from "../../services/userService";
import { getPrograms } from "../../services/programService";
import { processErrorResponse } from "../../helpers/processErrorResponse";
import { Button } from "../../ui/index";
import { useDispatch, useSelector } from "react-redux";
import useMobile from "../../hooks/useOrientation";
import { ValidateEmail, ValidatePhone } from "../shared/regex";
import { useLocation } from "react-router-dom";
import { OverlayNotification } from "../OverlayNotification";
import { CenteredLoader } from "../shared/CenteredLoader";
import { SessionContext } from "../../helpers/session";
import secureLocalStorage from "react-secure-storage";
import { primary } from "../../ui/colors";

const APP_BASE_URL = process.env.REACT_APP_APP_URL;

const primaryLight = "#e0faf7";
const primaryDark = "#00a896";

const TABS = [
  {
    id: "pasajeros",
    label: "Pasajeros",
    icon: (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: "datos",
    label: "Mis datos",
    icon: (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    id: "seguridad",
    label: "Seguridad",
    icon: (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
  },
];

const getInitials = (name = "", lastName = "") =>
  `${name.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const ProfileScreen = () => {
  const [activeTab, setActiveTab] = useState("pasajeros");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useContext(SessionContext);
  const [passengers, setPassengers] = useState();
  const [isEditUserDataModalOpen, setIsEditUserDataModalOpen] = useState(false);
  const [editMessage, setEditMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [userTrips, setUserTrips] = useState([]);
  const [programs, setPrograms] = useState([]);
  const storePassengers = useSelector((state) => state.passengers);

  const navigate = useNavigate();
  const isMobile = useMobile();
  const location = useLocation();
  const dispatch = useDispatch();

  const isPasswordInsecure = location?.state?.isPasswordInsecure ?? null;

  useEffect(() => {
    if (isPasswordInsecure) {
      setIsModalOpen(true);
    }
  }, []);

  useEffect(() => {
    setPassengers(storePassengers.passengers);
  }, [storePassengers]);

  const loadUserTrips = useCallback(() => {
    if (!user?.id) return Promise.resolve();
    return getUserTrips(user.id)
      .then((res) => setUserTrips(res || []))
      .catch((e) => console.log(e));
  }, [user?.id]);

  useEffect(() => {
    loadUserTrips();
  }, [loadUserTrips]);

  useEffect(() => {
    getPrograms()
      .then((res) => setPrograms(res || []))
      .catch((e) => console.log(e));
  }, []);

  // Trip confirmation is only available for programs that enable it.
  const isTripConfirmationEnabled = (programId) =>
    programs.some(
      (pr) => pr.id === programId && pr.tripConfirmationEnabled === true
    );

  if (!user) return null;

  const handleAddNewPassenger = () => navigate("/registro");

  const handleEditUserData = (userData) => {
    if (userData.phone && !ValidatePhone(userData.phone)) {
      setEditMessage("Telefono invalido");
    } else if (userData.email && !ValidateEmail(userData.email)) {
      setEditMessage("Email invalido");
    } else if (userData.email || userData.phone) {
      const email = userData.email === user.email ? undefined : userData.email;
      const phone = userData.phone === user.phone ? undefined : userData.phone;
      if (!email && !phone) {
        setEditMessage("Debe editar el email o el telefono.");
      } else {
        setIsLoading(true);
        editUser(userData.id, email, phone)
          .then((res) => {
            secureLocalStorage.setItem("user", JSON.stringify(res));
            setEditMessage("Editado exitosamente");
          })
          .catch((e) => setEditMessage(processErrorResponse(e)[1]))
          .finally(() => setIsLoading(false));
      }
    } else {
      setEditMessage("Debe editar el email o el telefono.");
    }
  };

  const handleCloseEditModal = () => {
    setEditMessage("");
    setIsEditUserDataModalOpen(false);
  };

  return (
    <>
      <OverlayNotification />
      {isLoading ? (
        <CenteredLoader />
      ) : (
        <div style={styles.container}>
          {/* User header */}
          <div style={styles.userHeader}>
            <div style={styles.avatar}>
              {getInitials(user.name, user.lastName)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={styles.userName}>{user.name} {user.lastName}</div>
              <div style={styles.userEmail}>{user.email}</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={styles.tabs}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                style={{
                  ...styles.tab,
                  ...(activeTab === tab.id ? styles.tabActive : {}),
                }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {!isMobile && <span>{tab.label}</span>}
                {isMobile && <span style={{ fontSize: 12 }}>{tab.label}</span>}
              </button>
            ))}
          </div>

          {/* Tab: Pasajeros */}
          {activeTab === "pasajeros" && (
            <div>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>Pasajeros</h2>
                <button style={styles.btnOutline} onClick={handleAddNewPassenger}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Agregar
                </button>
              </div>

              {passengers?.length === 0 ? (
                <p style={{ color: "#666", marginTop: 16 }}>No hay pasajeros creados.</p>
              ) : (
                <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))" }}>
                  {passengers?.map((p, i) => (
                    <PassengerCard
                      key={i}
                      p={p}
                      trip={userTrips.find((t) => t.passengerId === p.id)}
                      isTripConfirmationEnabled={isTripConfirmationEnabled}
                      onTripConfirmed={loadUserTrips}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Mis datos */}
          {activeTab === "datos" && (
            <div>
              <h2 style={{ ...styles.sectionTitle, marginBottom: 16 }}>Mis datos</h2>
              <div style={styles.card}>
                {[
                  { label: "Nombre completo", value: `${user.name} ${user.lastName}` },
                  { label: "Email", value: user.email },
                  { label: "Teléfono", value: user.phone },
                ].map(({ label, value }) => (
                  <div key={label} style={styles.dataRow}>
                    <span style={styles.dataLabel}>{label}</span>
                    <span style={styles.dataValue}>{value}</span>
                  </div>
                ))}
                <button
                  style={{ ...styles.btnOutline, marginTop: 16 }}
                  onClick={() => setIsEditUserDataModalOpen(true)}
                >
                  Editar datos
                </button>
              </div>
            </div>
          )}

          {/* Tab: Seguridad */}
          {activeTab === "seguridad" && (
            <div>
              <h2 style={{ ...styles.sectionTitle, marginBottom: 16 }}>Seguridad</h2>
              <div style={{ ...styles.card, flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111" }}>Contraseña</div>
                  <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>Cambiá tu contraseña de acceso</div>
                </div>
                <button style={styles.btnOutline} onClick={() => setIsModalOpen(true)}>
                  Cambiar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <ChangePasswordModal onCloseModal={() => setIsModalOpen(false)} user={user} />
      )}
      {isEditUserDataModalOpen && (
        <EditUserDataModal
          onCloseModal={handleCloseEditModal}
          onEdituser={handleEditUserData}
          user={user}
          editMessage={editMessage}
          loading={isLoading}
        />
      )}
    </>
  );
};

export default ProfileScreen;

// ---- Styles ----

const styles = {
  container: {
    padding: "16px",
    maxWidth: 720,
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  userHeader: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 24,
  },
  avatar: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    background: primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 20,
    color: "#fff",
    flexShrink: 0,
  },
  userName: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
  },
  userEmail: {
    fontSize: 20,
    color: "#666",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  tabs: {
    display: "flex",
    gap: 8,
    marginBottom: 24,
  },
  tab: {
    flex: 1,
    padding: "10px 8px",
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    background: "#f7f7f7",
    color: "#666",
    fontSize: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontWeight: 500,
    fontFamily: "Gilroy",
    transition: "all 0.15s",
  },
  tabActive: {
    background: primaryLight,
    borderColor: primary,
    color: primaryDark,
    fontWeight: 700,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
  },
  btnOutline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    border: `1.5px solid ${primary}`,
    borderRadius: 8,
    background: "transparent",
    color: primaryDark,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Gilroy",
  },
  grid: {
    display: "grid",
    gap: 16,
  },
  card: {
    background: "#fff",
    border: "1.5px solid #e0e0e0",
    borderRadius: 12,
    padding: 18,
    display: "flex",
    flexDirection: "column",
  },
  dataRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "13px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  dataLabel: {
    fontSize: 13,
    color: "#666",
  },
  dataValue: {
    fontSize: 15,
    color: "#111",
    fontWeight: 500,
  },
};

// ---- PassengerCard ----

const PassengerCard = ({
  p,
  trip,
  isTripConfirmationEnabled,
  onTripConfirmed,
}) => {
  const [showLink, setShowLink] = useState(false);

  const initials = getInitials(p.name, p.lastName);
  const showTripConfirmation = isTripConfirmationEnabled(
    trip?.programId ?? p.programId
  );
  const isEgresados = p?.programName?.toLowerCase()?.includes("egresados");

  return (
    <div
      style={{
        ...styles.card,
        gap: 14,
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = primary;
        e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,210,183,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e0e0e0";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ ...styles.avatar, width: 44, height: 44, fontSize: 12 }}>
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#111", fontFamily: "Gilroy-Bold" }}>
            {p.name} {p.lastName}
          </div>
          <span style={badgeStyle}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
            </svg>
            {p.programName}
          </span>
        </div>
      </div>

      {/* Details */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 4px" }}>
        <span style={styles.dataLabel}>Programa</span>
        <span style={{ ...styles.dataValue, textAlign: "right" }}>{p.tripName}</span>
        <span style={styles.dataLabel}>Grupo</span>
        <span style={{ ...styles.dataValue, textAlign: "right" }}>{p.groupName}</span>
      </div>

      {/* Trip confirmation */}
      {showTripConfirmation && trip && (
        <TripConfirmation
          passengerId={p.id}
          programId={trip.programId ?? p.programId}
          groupName={trip.groupName ?? p.groupName}
          currentTripId={trip.tripId ?? p.tripId}
          status={trip.tripConfirmationStatus}
          requestedTripName={trip.requestedTripName ?? p.requestedTripName}
          onConfirmed={onTripConfirmed}
        />
      )}

      {/* Share group preferences: stays editable after confirming the trip */}
      {showTripConfirmation && (
        <ShareGroupPreferences passengerId={p.id} />
      )}

      {/* Invite button */}
      {isEgresados && (
        <button
          style={{
            ...styles.btnOutline,
            width: "100%",
            justifyContent: "center",
            borderRadius: 8,
          }}
          onClick={() => setShowLink(!showLink)}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
            <line x1="19" y1="8" x2="19" y2="14"/>
            <line x1="16" y1="11" x2="22" y2="11"/>
          </svg>
          Invitar acompañante
        </button>
      )}

      {showLink && (
        <div style={{ fontSize: 12, color: "#444" }}>
          <strong>Enviá el siguiente link al acompañante: </strong>
          <a
            href={`${APP_BASE_URL}/registro/${p.documentId}`}
            style={{ color: primaryDark }}
          >
            Invitá a tu acompañante
          </a>
        </div>
      )}
    </div>
  );
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 13,
  fontWeight: 500,
  marginTop: 4,
  background: primaryLight,
  color: primaryDark,
  border: `1px solid ${primary}`,
};
